import { useEffect, useState } from 'react';
import { Shield, ShieldCheck, ShieldAlert, ShieldX, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getSubscriptionStatus } from '@/services/subscriptionApi';
import type { SubscriptionStatus } from '@/types/subscription.types';
import { Link } from 'react-router-dom';

export function SubscriptionStatusCard() {
  const [status, setStatus] = useState<SubscriptionStatus | null | 'loading'>('loading');

  useEffect(() => {
    getSubscriptionStatus()
      .then((data) => setStatus(data))
      .catch(() => setStatus(null));
  }, []);

  if (status === 'loading') {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center justify-center text-gray-400 text-sm">
            加载中...
          </div>
        </CardContent>
      </Card>
    );
  }

  const isSubscribed = status !== null && status.isValid;
  const isExpired = status !== null && status.status === 'expired';

  const config = {
    none: {
      icon: <ShieldX className="w-6 h-6" />,
      title: '未订阅',
      desc: '您当前没有有效的订阅',
      colorClass: 'text-gray-500',
      bgClass: 'bg-gray-50',
      borderClass: 'border-gray-200',
    },
    active: {
      icon: <ShieldCheck className="w-6 h-6" />,
      title: status?.planName || '已订阅',
      desc: status?.expiresAt
        ? `有效期至 ${formatDate(status.expiresAt)}`
        : '永久有效',
      colorClass: 'text-green-600',
      bgClass: 'bg-green-50',
      borderClass: 'border-green-200',
    },
    expired: {
      icon: <ShieldAlert className="w-6 h-6" />,
      title: '已过期',
      desc: status?.expiresAt
        ? `已于 ${formatDate(status.expiresAt)} 过期`
        : '订阅已过期',
      colorClass: 'text-red-600',
      bgClass: 'bg-red-50',
      borderClass: 'border-red-200',
    },
  };

  const current = isSubscribed ? config.active : isExpired ? config.expired : config.none;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Shield className="w-5 h-5 text-gray-600" />
          订阅状态
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          className={`flex items-center gap-4 p-4 rounded-lg border ${current.bgClass} ${current.borderClass}`}
        >
          <div className={`${current.colorClass}`}>{current.icon}</div>
          <div className="flex-1 min-w-0">
            <p className={`font-semibold ${current.colorClass}`}>{current.title}</p>
            <p className="text-sm text-gray-600 mt-0.5">{current.desc}</p>
          </div>
        </div>

        {!isSubscribed && (
          <Link to="/redeem">
            <Button className="w-full">
              兑换邀请码
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
